import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    TextField,
    Autocomplete,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableFooter,
    TableRow,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    IconButton,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Grid,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, startOfMonth, endOfMonth, addDays, addMonths } from 'date-fns';
import apiService from '../../services/apiService';
import AddCustomerDialog from './AddCustomerDialog';

const filter = createFilterOptions();

const DailyCollections = () => {
    const [customers, setCustomers] = useState([]);
    const [collections, setCollections] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [monthDays, setMonthDays] = useState([]);
    const [windowStartIdx, setWindowStartIdx] = useState(0);
    const initializedWindowRef = useRef(false);
    const customerInputRef = useRef(null);
    const billNoInputRef = useRef(null);
    const [displayOldBalance, setDisplayOldBalance] = useState('0');
    const [formData, setFormData] = useState({
        pageNo: '',
        customer: null,
        billNo: '',
        billAmount: '',
        oldBalance: '0',
        received: '',
        comments: '',
        lessAmount: '0',
        billTypeFlag: 'D',
        isPrinted: 'N',
        isBillGenerated: 'Y',
        remarks: ''
    });
    const [customerInputValue, setCustomerInputValue] = useState('');
    const [errors, setErrors] = useState({});

    // Add Customer Dialog State
    const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));


    const fetchCustomers = useCallback(async () => {
        try {
            const response = await apiService.customers.getMinDetails();
            if (Array.isArray(response.data)) {
                setCustomers(response.data);
            } else {
                console.error('Customers data is not an array:', response.data);
                setCustomers([]);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setCustomers([]);
        }
    }, []);

    const fetchTodayCollections = useCallback(async () => {
        try {
            const response = await apiService.collections.getTodayCollections({ date: selectedDate, billTypeFlag: 'D' });
            setCollections(response.data);
        } catch (error) {
            console.error('Error fetching collections:', error);
        }
    }, [selectedDate]);

    useEffect(() => {
        fetchCustomers();
        fetchTodayCollections();
    }, [fetchCustomers, fetchTodayCollections]);

    useEffect(() => {
        // Focus the customer input on initial load
        if (customerInputRef.current) {
            customerInputRef.current.focus();
        }
    }, []);

    // Build month days for current month and on first load place window to include today's date.
    // Afterwards, only clamp when month changes (no auto-centering on selection).
    useEffect(() => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const days = [];
        for (let d = start; d <= end; d = addDays(d, 1)) {
            days.push(format(d, 'yyyy-MM-dd'));
        }
        setMonthDays(days);
        const maxStart = Math.max(days.length - 7, 0);

        if (!initializedWindowRef.current) {
            const idx = days.indexOf(selectedDate);
            if (idx !== -1) {
                const startIdx = Math.max(0, Math.min(idx - 3, maxStart));
                setWindowStartIdx(startIdx);
            } else {
                setWindowStartIdx(0);
            }
            initializedWindowRef.current = true;
        } else {
            // Clamp current windowStartIdx within new bounds
            setWindowStartIdx((prev) => Math.max(0, Math.min(prev, maxStart)));
        }
    }, [currentMonth, selectedDate]);

    // fetchCustomers and fetchTodayCollections memoized above

    // Navigation: shift the 7-day window by one day, crossing months if needed
    const handlePrevDay = () => {
        if (windowStartIdx - 1 >= 0) {
            setWindowStartIdx(windowStartIdx - 1);
        } else {
            const prevMonth = addMonths(currentMonth, -1);
            // compute previous month length
            const prevStart = startOfMonth(prevMonth);
            const prevEnd = endOfMonth(prevMonth);
            let count = 0;
            for (let d = prevStart; d <= prevEnd; d = addDays(d, 1)) count++;
            setCurrentMonth(prevMonth);
            setWindowStartIdx(Math.max(count - 7, 0));
        }
    };

    

    const handleNextDay = () => {
        const maxStart = Math.max(monthDays.length - 7, 0);
        if (windowStartIdx + 1 <= maxStart) {
            setWindowStartIdx(windowStartIdx + 1);
        } else {
            const nextMonth = addMonths(currentMonth, 1);
            setCurrentMonth(nextMonth);
            setWindowStartIdx(0);
        }
    };


    const handleCustomerChange = async (event, newValue) => {
        if (newValue?.isAddNew) {
            setAddCustomerDialogOpen(true);
            return;
        }
        if (errors.customer) {
            setErrors(prev => ({ ...prev, customer: undefined }));
        }
        if (newValue) {
            try {
                // Fetch precise balances for the selected customer
                const resp = await apiService.customers.getBalance(newValue.customerId);
                const apiOldBalance = parseFloat(resp?.data?.cumulativeBalance ?? newValue.balanceAmount ?? 0);
                const apiCumulative = parseFloat(resp?.data?.cumulativeBalance ?? apiOldBalance);

                // For calculations, we'll use the oldBalance from the API
                // For display, we'll show the cumulative balance
                setFormData(prev => ({
                    ...prev,
                    customer: newValue,
                    oldBalance: String(apiOldBalance), // used for calculations
                    pageNo: newValue.pageNo || ''
                }));
                // Show the cumulative balance in the UI
                setDisplayOldBalance(apiCumulative.toFixed(2));
            } catch (error) {
                console.error('Error fetching customer balance:', error);
                // Fallback to provided minimal details
                const fallbackBalance = parseFloat(newValue.balanceAmount ?? 0);
                setFormData(prev => ({
                    ...prev,
                    customer: newValue,
                    oldBalance: String(fallbackBalance),
                    pageNo: newValue.pageNo || ''
                }));
                // Use the same value for display in case of error
                setDisplayOldBalance(fallbackBalance.toFixed(2));
            }
            // Move focus to Bill No for quicker data entry
            requestAnimationFrame(() => {
                billNoInputRef.current?.focus();
            });
        } else {
            setFormData(prev => ({
                ...prev,
                customer: null,
                oldBalance: '0',
                pageNo: ''
            }));
            setDisplayOldBalance('0');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const calculateTotal = () => {
        const billAmount = parseFloat(formData.billAmount) || 0;
        // Use displayOldBalance for the calculation since that's what's shown in the UI
        const oldBalance = parseFloat(displayOldBalance) || 0;
        const total = oldBalance + billAmount;
        return total.toFixed(2);
    };

    const calculateBalance = () => {
        const total = parseFloat(calculateTotal()) || 0;
        const received = parseFloat(formData.received) || 0;
        const less = parseFloat(formData.lessAmount) || 0; // Include lessAmount
        return (total - received - less).toFixed(2); // Subtract lessAmount
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;
        if (!formData.customer?.customerId) {
            newErrors.customer = 'Please select a customer';
            isValid = false;
        }
        if (formData.received === '' || formData.received === null || formData.received === undefined) {
            newErrors.received = 'Enter amount';
            isValid = false;
        }
        setErrors(newErrors);
        return isValid;
    };

    const saveCollection = async () => {
        if (!validateForm()) return false;
        try {
            const billAmount = parseFloat(formData.billAmount || 0);
            const oldBalance = parseFloat(formData.oldBalance || 0);
            const total = billAmount + oldBalance;
            const received = parseFloat(formData.received || 0);
            const less = parseFloat(formData.lessAmount || 0);
            const computedIsPaid = (received + less) >= total ? 'Y' : 'N';

            const payload = {
                pageNo: formData.pageNo,
                manualBillNo: formData.billNo || undefined,
                customerId: formData.customer?.customerId || undefined,
                billAmount: formData.billAmount || undefined,
                oldBalance: displayOldBalance || undefined,
                receivedAmount: formData.received || undefined,
                cumulativeBalance: calculateBalance() || undefined,
                updateUser: "currentUser",
                isBillGenerated: 'Y',
                isPrinted: formData.isPrinted || undefined,
                billTypeFlag: 'D',
                isPaid: computedIsPaid,
                updateTimestamp: new Date().toISOString(),
                remarks: formData.remarks ?? '',
                lessAmount: formData.lessAmount || undefined
            };

            const clean = Object.fromEntries(
                Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null)
            );

            await apiService.collections.saveCollection(clean);
            fetchTodayCollections();
            setFormData({
                pageNo: '',
                customer: null,
                billNo: '',
                billAmount: '',
                oldBalance: '0',
                received: '',
                comments: '',
                lessAmount: '0',
                billTypeFlag: 'D',
                isPrinted: 'N',
                isBillGenerated: 'Y',
                remarks: ''
            });
            setDisplayOldBalance('0');
            setCustomerInputValue('');
            // Refocus on Customer for next entry
            if (customerInputRef.current) {
                customerInputRef.current.focus();
            }
            return true;
        } catch (error) {
            console.error('Error saving collection:', error);
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await saveCollection();
    };

    const commonTextFieldStyles = {
        InputProps: {
            style: { fontSize: '0.875rem' }
        },
        InputLabelProps: {
            style: { fontSize: '0.875rem' },
            shrink: true
        },
        sx: {
            '& .MuiInputLabel-root': {
                lineHeight: '1em',
            },
            '& .MuiOutlinedInput-root': {
                '& input': {
                    padding: '8px 4px'  // Reduced left/right padding (was 14px by default)
                }
            }
        }
    };

    const autocompleteStyles = {
        '& .MuiOutlinedInput-root': {
            padding: '0px 4px !important'  // Reduced left/right padding
        },
        '& .MuiAutocomplete-input': {
            padding: '8px 4px !important'  // Reduced left/right padding
        }
    };

    const handleEnterKey = async (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            await saveCollection();
        }
    };

    const handleEditClick = (row) => {
        setEditingId(row.billSummaryId);
        setEditData({
            pageNo: row.pageNo || '',
            manualBillNo: row.manualBillNo || '',
            billAmount: row.billAmount || '',
            receivedAmount: row.receivedAmount || '',
            lessAmount: row.lessAmount || '',
            remarks: row.remarks || ''
        });
    };

    const validateEditData = () => {
        const row = collections.find(r => r.billSummaryId === editingId) || {};
        const billAmount = parseFloat(editData.billAmount ?? row.billAmount ?? 0) || 0;
        const oldBalance = parseFloat(row.oldBalance ?? 0) || 0;
        const total = billAmount + oldBalance;
        const receivedAmount = parseFloat(editData.receivedAmount ?? row.receivedAmount ?? 0) || 0;
        const lessAmount = parseFloat(editData.lessAmount ?? row.lessAmount ?? 0) || 0;

        if (receivedAmount + lessAmount > total) {
            alert('Total of received and less amount cannot exceed total');
            return false;
        }
        return true;
    };

    const handleEditSave = async (id) => {
        if (!validateEditData()) return;

        try {
            const row = collections.find(r => r.billSummaryId === id) || {};
            const computedIsPaid = (() => {
                const billAmt = parseFloat(editData.billAmount ?? row.billAmount ?? 0) || 0;
                const receivedAmt = parseFloat(editData.receivedAmount ?? row.receivedAmount ?? 0) || 0;
                const lessAmt = parseFloat(editData.lessAmount ?? row.lessAmount ?? 0) || 0;
                return receivedAmt + lessAmt >= billAmt ? 'Y' : 'N';
            })();

            // Normalize lessAmount to never be null/empty; default to '0.00' when not provided
            const normalizedLessAmount = (val) => {
                if (val === undefined || val === null || val === '') return '0.00';
                return String(val);
            };

            const updatePayload = {
                billSummaryId: id,
                pageNo: editData.pageNo,
                manualBillNo: editData.manualBillNo,
                billAmount: editData.billAmount,
                receivedAmount: editData.receivedAmount,
                lessAmount: normalizedLessAmount(editData.lessAmount),
                remarks: editData.remarks ?? '',
                customerId: row.customerId,
                oldBalance: row.oldBalance,
                isBillGenerated: formData.isBillGenerated || row.isBillGenerated,
                isPrinted: formData.isPrinted || row.isPrinted,
                billTypeFlag: formData.billTypeFlag || row.billTypeFlag,
                isPaid: computedIsPaid,
                updateTimestamp: new Date().toISOString(),
            };

            const cleanUpdate = Object.fromEntries(
                Object.entries(updatePayload).filter(([_, v]) => v !== undefined && v !== null)
            );

            await apiService.collections.saveCollection(cleanUpdate);
            setEditingId(null);
            setEditData({});
            fetchTodayCollections();
        } catch (error) {
            console.error('Error updating collection:', error);
            alert('Failed to update the collection');
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditCancel = () => {
        setEditingId(null);
        setEditData({});
    };

    const textFieldStyle = {
        '& .MuiOutlinedInput-root': {
            '& input': {
                padding: '4px', // Reduced padding for the input
            },
            '& fieldset': {
                borderRadius: '4px', // Optional: reduces the border radius
            }
        }
    };

    // --- Add Customer Dialog Logic ---

    const handleSaveNewCustomer = async (newCustomer) => {
        // Refresh customer list
        await fetchCustomers();

        // Select the new customer
        setFormData(prev => ({
            ...prev,
            customer: newCustomer,
            oldBalance: '0', // New customer starts with 0 balance
            pageNo: newCustomer.bookPage || ''
        }));
        setDisplayOldBalance('0');
        setCustomerInputValue(''); // Clear input value so the selected customer displays correctly

        setAddCustomerDialogOpen(false);

        // Focus on Bill No
        requestAnimationFrame(() => {
            billNoInputRef.current?.focus();
        });
    };

    const renderMobileCard = (row) => {
        const isEditing = editingId === row.billSummaryId;
        const matchedCustomer = (customers || []).find(c => c.customerId === row.customerId);
        const displayName = [
            row.customerNameTamil || matchedCustomer?.customerNameTamil || row.customerName,
            row.customerCity || matchedCustomer?.customerCity
        ].filter(Boolean).join(' - ');

        if (isEditing) {
            return (
                <Card key={row.billSummaryId} sx={{ mb: 1.5, boxShadow: 3 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Editing: {displayName}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Page No"
                                    name="pageNo"
                                    value={editData.pageNo}
                                    onChange={handleEditChange}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Bill No"
                                    name="manualBillNo"
                                    value={editData.manualBillNo}
                                    onChange={handleEditChange}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Bill Amount"
                                    name="billAmount"
                                    type="number"
                                    value={editData.billAmount}
                                    onChange={handleEditChange}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Received"
                                    name="receivedAmount"
                                    type="number"
                                    value={editData.receivedAmount}
                                    onChange={handleEditChange}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Less"
                                    name="lessAmount"
                                    type="number"
                                    value={editData.lessAmount}
                                    onChange={handleEditChange}
                                    onFocus={() => {
                                        setEditData(prev => ({
                                            ...prev,
                                            lessAmount: (prev.lessAmount === '0' || prev.lessAmount === 0) ? '' : prev.lessAmount
                                        }));
                                    }}
                                    onBlur={() => {
                                        setEditData(prev => ({
                                            ...prev,
                                            lessAmount: prev.lessAmount === '' ? '0' : prev.lessAmount
                                        }));
                                    }}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Remarks"
                                    name="remarks"
                                    value={editData.remarks}
                                    onChange={handleEditChange}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button variant="contained" size="small" onClick={() => handleEditSave(row.billSummaryId)}>Save</Button>
                            <Button variant="outlined" size="small" onClick={handleEditCancel}>Cancel</Button>
                        </Box>
                    </CardContent>
                </Card>
            );
        }

        return (
            <Card key={row.billSummaryId} sx={{ mb: 1, boxShadow: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Box sx={{ flex: 1, mr: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.95rem' }}>
                                {displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                Pg: {row.pageNo || '-'} | Bill: {row.manualBillNo || '-'}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => handleEditClick(row)} sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                        <Box>
                             <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>Bill</Typography>
                             <Typography variant="body2" fontWeight={600}>{(Number(row.billAmount)||0).toFixed(2)}</Typography>
                        </Box>
                        <Box>
                             <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>Old</Typography>
                             <Typography variant="body2" fontWeight={600}>{(Number(row.oldBalance)||0).toFixed(2)}</Typography>
                        </Box>
                        <Box>
                             <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>Total</Typography>
                             <Typography variant="body2" fontWeight={700}>{(parseFloat(row.billAmount || 0) + parseFloat(row.oldBalance || 0)).toFixed(2)}</Typography>
                        </Box>

                        <Box>
                             <Typography variant="caption" color="success.main" display="block" sx={{ lineHeight: 1 }}>Rcvd</Typography>
                             <Typography variant="body2" fontWeight={700} color="success.main">{(Number(row.receivedAmount)||0).toFixed(2)}</Typography>
                        </Box>
                        <Box>
                            {(Number(row.lessAmount) > 0) && (
                                <>
                                    <Typography variant="caption" color="warning.main" display="block" sx={{ lineHeight: 1 }}>Less</Typography>
                                    <Typography variant="body2" fontWeight={700} color="warning.main">{(Number(row.lessAmount)||0).toFixed(2)}</Typography>
                                </>
                            )}
                        </Box>
                        <Box>
                             <Typography variant="caption" color="error.main" display="block" sx={{ lineHeight: 1 }}>Bal</Typography>
                             <Typography variant="body2" fontWeight={700} color="error.main">{(Number(row.cumulativeBalance)||0).toFixed(2)}</Typography>
                        </Box>
                    </Box>
                    {row.remarks && (
                        <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                            Note: {row.remarks}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        );
    };


const renderTableRow = (row, index) => {
    const isEditing = editingId === row.billSummaryId;
    const matchedCustomer = (customers || []).find(c => c.customerId === row.customerId);
    const displayName = [
        row.customerNameTamil || matchedCustomer?.customerNameTamil || row.customerName,
        row.customerCity || matchedCustomer?.customerCity
    ].filter(Boolean).join(' - ');

 if (isEditing) {
            return (
                <TableRow key={row.billSummaryId || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                        <TextField
                            name="pageNo"
                            value={editData.pageNo}
                            onChange={handleEditChange}
                            size="small"
                            fullWidth
                            sx={textFieldStyle}
                        />
                    </TableCell>
                    <TableCell>{displayName}</TableCell>
                    <TableCell>
                        <TextField
                            name="manualBillNo"
                            value={editData.manualBillNo}
                            onChange={handleEditChange}
                            size="small"
                            fullWidth
                            sx={textFieldStyle}
                        />
                    </TableCell>
                    <TableCell>
                        <TextField
                            name="billAmount"
                            type="number"
                            value={editData.billAmount}
                            onChange={handleEditChange}
                            size="small"
                            fullWidth
                            sx={textFieldStyle}
                        />
                    </TableCell>
                    <TableCell>{row.oldBalance}</TableCell>
                    <TableCell>{parseFloat(editData.billAmount || 0) + parseFloat(row.oldBalance || 0)}</TableCell>
                    <TableCell>
                        <TextField
                            name="receivedAmount"
                            type="number"
                            value={editData.receivedAmount}
                            onChange={handleEditChange}
                            size="small"
                            fullWidth
                            sx={textFieldStyle}
                        />
                    </TableCell>
                    <TableCell>
                        <TextField
                            name="lessAmount"
                            type="number"
                            value={editData.lessAmount}
                            onChange={handleEditChange}
                            onFocus={() => {
                                setEditData(prev => ({
                                    ...prev,
                                    lessAmount: (prev.lessAmount === '0' || prev.lessAmount === 0) ? '' : prev.lessAmount
                                }));
                            }}
                            onBlur={() => {
                                setEditData(prev => ({
                                    ...prev,
                                    lessAmount: prev.lessAmount === '' ? '0' : prev.lessAmount
                                }));
                            }}
                            size="small"
                            fullWidth
                            sx={textFieldStyle}
                        />
                    </TableCell>
                    <TableCell>{calculateEditBalance(row.oldBalance)}</TableCell>
                    <TableCell>
                        <TextField
                            name="remarks"
                            value={editData.remarks}
                            onChange={handleEditChange}
                            size="small"
                            fullWidth
                            sx={textFieldStyle}
                        />
                    </TableCell>
                    <TableCell>
                        <IconButton
                            color="primary"
                            onClick={() => handleEditSave(row.billSummaryId)}
                            size="small"
                        >
                            <SaveIcon />
                        </IconButton>
                        <IconButton
                            color="secondary"
                            onClick={handleEditCancel}
                            size="small"
                        >
                            <CancelIcon />
                        </IconButton>
                    </TableCell>
                </TableRow>
            );
        }

        return (
            <TableRow key={row.billSummaryId || index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{row.pageNo}</TableCell>
                <TableCell>{displayName}</TableCell>
                <TableCell>{row.manualBillNo}</TableCell>
                <TableCell>{row.billAmount}</TableCell>
                <TableCell>{row.oldBalance}</TableCell>
                <TableCell>{parseFloat(row.billAmount || 0) + parseFloat(row.oldBalance || 0)}</TableCell>
                <TableCell>{row.receivedAmount}</TableCell>
                <TableCell>{row.lessAmount}</TableCell>
                <TableCell>{row.cumulativeBalance}</TableCell>
                <TableCell>{row.remarks}</TableCell>
                <TableCell>
                    <IconButton
                        color="primary"
                        onClick={() => handleEditClick(row)}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                </TableCell>
            </TableRow>
        );
    };

    const calculateEditBalance = (oldBalance) => {
        const billAmount = parseFloat(editData.billAmount) || 0;
        const receivedAmount = parseFloat(editData.receivedAmount) || 0;
        const lessAmount = parseFloat(editData.lessAmount) || 0;
        const total = billAmount + parseFloat(oldBalance || 0);
        return (total - receivedAmount - lessAmount).toFixed(2);
    };

    const totals = React.useMemo(() => {
        let received = 0, less = 0, balance = 0;
        (collections || []).forEach(r => {
            received += parseFloat(r.receivedAmount || 0);
            less += parseFloat(r.lessAmount || 0);
            const billAmt = parseFloat(r.billAmount || 0);
            const oldBal = parseFloat(r.oldBalance || 0);
            const rec = parseFloat(r.receivedAmount || 0);
            const les = parseFloat(r.lessAmount || 0);
            const rowBalance = r.cumulativeBalance !== undefined && r.cumulativeBalance !== null
                ? parseFloat(r.cumulativeBalance || 0)
                : (billAmt + oldBal - rec - les);
            balance += rowBalance;
        });
        return {
            received: received.toFixed(2),
            less: less.toFixed(2),
            balance: balance.toFixed(2)
        };
    }, [collections]);

    const formContent = (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
            <Box component="form" onSubmit={handleSubmit}
                 sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                 }}>
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'flex-start'
                }}>
                    <Box sx={{ flex: '1 1 70px', minWidth: 50 }}>
                        <TextField fullWidth label="Page No" name="pageNo" value={formData.pageNo} onChange={handleInputChange} onKeyDown={handleEnterKey} size="small" {...commonTextFieldStyles} />
                    </Box>
                    <Box sx={{ flex: '2 1 300px', minWidth: 200 }}>
                        <Autocomplete
                            fullWidth
                            disableClearable
                            forcePopupIcon={false}
                            autoHighlight
                            autoSelect
                            options={customers || []}
                            getOptionLabel={(option) => option ? `${option.customerId} - ${option.customerName} - ${option.customerNameTamil} - ${option.customerCity}` : ''}
                            value={formData.customer}
                            onChange={handleCustomerChange}
                            inputValue={customerInputValue}
                            filterOptions={(options, params) => {
                                const filtered = filter(options, params);
                                const { inputValue } = params;
                                // Suggest the creation of a new value
                                const isExisting = options.some((option) => inputValue === option.customerName);
                                if (inputValue !== '' && !isExisting) {
                                    filtered.push({
                                        customerId: 'ADD_NEW', customerName: `Add "${inputValue}"`, customerNameTamil: '', customerCity: '', isAddNew: true
                                    });
                                }
                                return filtered;
                            }}
                            onInputChange={(event, newInputValue) => setCustomerInputValue(newInputValue)}
                            sx={{
                                ...autocompleteStyles,
                                '& .MuiOutlinedInput-root': {
                                    padding: '0px 8px',
                                    paddingRight: '36px',
                                    minHeight: 36,
                                    alignItems: 'center'
                                },
                                '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                                '& .MuiAutocomplete-input': {
                                    padding: '8px 4px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minWidth: 0,
                                    flex: '1 1 auto',
                                    width: 0
                                },
                                '& .MuiAutocomplete-endAdornment': { right: 8 }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Customer"
                                    error={!!errors.customer}
                                    helperText={errors.customer}
                                    size="small"
                                    autoFocus
                                    inputRef={customerInputRef}
                                    InputProps={{ ...params.InputProps, style: { fontSize: '0.875rem' } }}
                                    InputLabelProps={{ style: { fontSize: '0.875rem' }, shrink: true }}
                                    sx={{
                                        '& .MuiInputLabel-root': { lineHeight: '1em' },
                                        ...commonTextFieldStyles.sx,
                                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                            borderWidth: '2px'
                                        }
                                    }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...otherProps } = props;
                                if (option.isAddNew) {
                                    return (
                                        <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                                            + {option.customerName}
                                        </li>
                                    );
                                }
                                return (
                                    <li key={key} {...otherProps}>
                                        {`${option.customerId} - ${option.customerName} - ${option.customerNameTamil} - ${option.customerCity}`}
                                    </li>
                                );
                            }}
                            isOptionEqualToValue={(option, value) => option.customerId === value?.customerId}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 70px', minWidth: 50 }}>
                        <TextField
                            fullWidth
                            label="Bill No"
                            name="billNo"
                            value={formData.billNo}
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    document.querySelector('input[name="billAmount"]')?.focus();
                                }
                            }}
                            size="small"
                            inputRef={billNoInputRef}
                            {...commonTextFieldStyles}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 90px', minWidth: 80 }}>
                        <TextField 
                            fullWidth 
                            label="Bill Amount" 
                            name="billAmount" 
                            type="number" 
                            value={formData.billAmount} 
                            onChange={handleInputChange} 
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    document.querySelector('input[name="received"]')?.focus();
                                }
                            }}
                            inputProps={{ step: '0.01' }} 
                            size="small" 
                            {...commonTextFieldStyles} 
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 70px', minWidth: 80 }}>
                        <TextField fullWidth label="Old Balance" value={displayOldBalance} disabled size="small" {...commonTextFieldStyles} />
                    </Box>
                    <Box sx={{ flex: '1 1 70px', minWidth: 60 }}>
                        <TextField fullWidth label="Total" value={calculateTotal()} disabled size="small" {...commonTextFieldStyles} />
                    </Box>
                    <Box sx={{ flex: '1 1 70px', minWidth: 70 }}>
                        <TextField fullWidth label="Received" name="received" type="number" value={formData.received} onChange={handleInputChange} onKeyDown={handleEnterKey} inputProps={{ step: '0.01' }} size="small" error={!!errors.received} helperText={errors.received} {...commonTextFieldStyles} />
                    </Box>
                    <Box sx={{ flex: '1 1 70px', minWidth: 80 }}>
                        <TextField
                            fullWidth
                            label="Less Amount"
                            name="lessAmount"
                            type="number"
                            value={formData.lessAmount}
                            onChange={handleInputChange}
                            onFocus={() => {
                                setFormData(prev => ({
                                    ...prev,
                                    lessAmount: (prev.lessAmount === '0' || prev.lessAmount === 0) ? '' : prev.lessAmount
                                }));
                            }}
                            onBlur={() => {
                                setFormData(prev => ({
                                    ...prev,
                                    lessAmount: prev.lessAmount === '' ? '0' : prev.lessAmount
                                }));
                            }}
                            onKeyDown={handleEnterKey}
                            inputProps={{ step: '0.01' }}
                            size="small"
                            {...commonTextFieldStyles}
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 70px', minWidth: 70 }}>
                        <TextField fullWidth label="Balance" value={calculateBalance()} disabled size="small" {...commonTextFieldStyles} />
                    </Box>
                    <Box sx={{ flex: '1 1 70px', minWidth: 50 }}>
                        <TextField fullWidth label="Comments" name="remarks" value={formData.remarks} onChange={handleInputChange} onKeyDown={handleEnterKey} size="small" maxRows={1} {...commonTextFieldStyles} />
                    </Box>
                    <Box sx={{ flex: '0 1 70px', minWidth: 50, display: 'flex' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            fullWidth
                            sx={{ minHeight: 36 }}>Save</Button>
                    </Box>
                </Box>
            </Box>
        </Paper>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
            <Paper sx={{ pt: 2, pb: 0.75, px: 1.5, mb: 1 }}>
                <Box sx={{
                    position: 'relative',
                    mb: 0.5,
                    minHeight: { xs: 'auto', sm: 38 },
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: { xs: 'space-between', sm: 'center' },
                    gap: 0
                }}>
                    <Box sx={{ position: { xs: 'static', sm: 'absolute' }, left: { sm: 8 }, top: { sm: 6 }, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                            Daily Collections
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, mt: 0.25 }}>
                            {format(new Date(), 'PPP')}
                        </Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, opacity: 0.85, textAlign: 'center' }}>
                        {format(new Date(selectedDate), 'MMMM yyyy')}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.25 }}>
                    <IconButton aria-label="previous day" onClick={handlePrevDay} size="small">
                        <ChevronLeftIcon />
                    </IconButton>
                    <ToggleButtonGroup
                        value={selectedDate}
                        exclusive
                        onChange={(e, newDate) => { if (newDate) setSelectedDate(newDate); }}
                        aria-label="date selection"
                        sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            gap: 0.5,
                            mb: 0,
                            overflowX: 'hidden',
                            overflowY: 'hidden',
                            justifyContent: 'center'
                        }}
                    >
                        {monthDays
                            .slice(windowStartIdx, windowStartIdx + 7)
                            .map((date) => {
                                const jsDate = new Date(date);
                                const isSelected = selectedDate === date;
                                const isToday = format(new Date(), 'yyyy-MM-dd') === date;
                                const weekday = format(jsDate, 'EEE');
                                const day = format(jsDate, 'dd');
                                const mon = format(jsDate, 'MMM');
                                return (
                                    <Tooltip key={date} title={format(jsDate, 'PPP')} arrow>
                                        <ToggleButton
                                            value={date}
                                            sx={{
                                                minWidth: '68px',
                                                minHeight: 56,
                                                px: 0.9,
                                                py: 0.6,
                                                borderRadius: 3,
                                                borderColor: 'transparent',
                                                overflow: 'hidden',
                                                background: isSelected
                                                    ? 'linear-gradient(135deg, #64b5f6 0%, #90caf9 100%)'
                                                    : isToday
                                                    ? 'rgba(25, 118, 210, 0.10)'
                                                    : 'rgba(25,118,210,0.06)',
                                                color: isSelected ? '#0d1b2a' : 'rgba(0,0,0,0.78)',
                                                boxShadow: isSelected ? '0 4px 12px rgba(100,181,246,0.45)' : 'none',
                                                transition: 'all 200ms ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    background: isSelected
                                                        ? 'linear-gradient(135deg, #42a5f5 0%, #64b5f6 100%)'
                                                        : 'rgba(25,118,210,0.12)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                                <Typography variant="caption" sx={{ opacity: isSelected ? 0.95 : 0.8, lineHeight: 1.1 }}>
                                                    {weekday}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                                    {day} <Typography component="span" variant="caption" sx={{ ml: 0.3 }}>{mon}</Typography>
                                                </Typography>
                                            </Box>
                                        </ToggleButton>
                                    </Tooltip>
                                );
                            })}
                    </ToggleButtonGroup>
                    <IconButton aria-label="next day" onClick={handleNextDay} size="small">
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </Paper>

            {!isMobile && formContent}

            <Box sx={{ flex: 1, minHeight: 0 }}>
                {isMobile ? (
                    <Box sx={{ overflowY: 'auto', height: '100%', pb: 2 }}>
                        {formContent}
                        {collections && collections.length > 0 ? (
                            <>
                                {collections.map((row) => renderMobileCard(row))}
                                <Paper sx={{ p: 1.5, mt: 1, bgcolor: '#e3f2fd', position: 'sticky', bottom: 0, zIndex: 10 }}>
                                   <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Box>
                                         <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>Total Rcvd</Typography>
                                         <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>{totals.received}</Typography>
                                      </Box>
                                      <Box>
                                         <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>Total Less</Typography>
                                         <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.main' }}>{totals.less}</Typography>
                                      </Box>
                                      <Box>
                                         <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>Total Bal</Typography>
                                         <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>{totals.balance}</Typography>
                                      </Box>
                                   </Box>
                                </Paper>
                            </>
                        ) : (
                            <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    No data available
                                </Typography>
                                <Typography variant="body2">
                                    No collections found for {format(new Date(selectedDate), 'PPP')}.
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                ) : (
                    collections && collections.length > 0 ? (
                        <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
                        <Table stickyHeader size="small">
                            <TableHead sx={{
                                '& .MuiTableCell-head': {
                                    backgroundColor: '#e3f2fd',
                                    fontWeight: 700,
                                    borderBottom: '1px solid #bbdefb'
                                }
                            }}>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>Page No</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Bill No</TableCell>
                                    <TableCell>Bill Amount</TableCell>
                                    <TableCell>Old Balance</TableCell>
                                    <TableCell>Total</TableCell>
                                    <TableCell>Received</TableCell>
                                    <TableCell>Less Amount</TableCell>
                                    <TableCell>Balance</TableCell>
                                    <TableCell>Comments</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {collections.map((row, index) => renderTableRow(row, index))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{totals.received}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{totals.less}</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>{totals.balance}</TableCell>
                                    <TableCell />
                                    <TableCell />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                    ) : (
                        <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                No data available
                            </Typography>
                            <Typography variant="body2">
                                No collections found for {format(new Date(selectedDate), 'PPP')}.
                            </Typography>
                        </Paper>
                    )
                )}
            </Box>

            <AddCustomerDialog
                open={addCustomerDialogOpen}
                onClose={() => setAddCustomerDialogOpen(false)}
                onSaveSuccess={handleSaveNewCustomer}
            />
        </Box>
    );
};

export default DailyCollections;
